import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Pages
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import LandingPage from '@/pages/LandingPage';
import BlogListPage from '@/pages/BlogListPage';
import BlogPostPage from '@/pages/BlogPostPage';
import DashboardPage from '@/pages/DashboardPage';
import ProjectPage from '@/pages/ProjectPage';
import ReportsPage from '@/pages/ReportsPage';
import PeoplePage from '@/pages/PeoplePage';
import SettingsPage from '@/pages/SettingsPage';
import PersonReportPage from '@/pages/PersonReportPage';

// Components
import Layout from '@/components/Layout';
import TimerWidget from '@/components/TimerWidget';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const { isAuthenticated, validateSession, _hasHydrated } = useAuthStore();
  const { fetchOrganizations, initializeTimer } = useAppStore();
  const [isReady, setIsReady] = useState(false);

  // Validate session on app load
  useEffect(() => {
    const validate = async () => {
      if (!_hasHydrated) {
        // Wait for hydration
        return;
      }

      // Now that we're hydrated, validate the session
      await validateSession();
      setIsReady(true);
    };

    validate();
  }, [_hasHydrated, validateSession]);

  useEffect(() => {
    if (isAuthenticated && isReady) {
      fetchOrganizations();
      initializeTimer();
    }
  }, [isAuthenticated, isReady, fetchOrganizations, initializeTimer]);

  // Show loading screen while waiting for hydration and validation
  if (!isReady) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/projects/:projectId" element={<ProjectPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/person-report" element={<PersonReportPage />} />
                    <Route path="/people" element={<PeoplePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
        {isAuthenticated && <TimerWidget />}
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
